import { useCallback, useRef, useState } from 'react';
import type { ApprovalChainEditorTarget } from '../ApprovalChainEditorPanel';

export type ApprovalChainsDrawerView =
  | { kind: 'closed' }
  | { kind: 'list' }
  | { kind: 'editor'; target: ApprovalChainEditorTarget };

/**
 * Owns which subview the approval chains drawer shows and the unsaved-changes
 * guard over every move out of the editor.
 *
 * The guard has to live out here rather than inside the editor because leaving
 * the editor takes several shapes — closing the drawer, going back to the list,
 * re-targeting onto another chain, landing a copy — and every one of them
 * discards edits. Those are the host's decisions, so the host has to be the
 * thing that asks.
 *
 * `renderKey` exists because `ApprovalChainForm` reads `initialValues` as
 * `defaultValues` only: every editor open must be a fresh mount, including
 * create -> copy, where the target shape alone does not change.
 */
export function useApprovalChainsDrawer() {
  const [view, setView] = useState<ApprovalChainsDrawerView>({ kind: 'closed' });
  const [renderKey, setRenderKey] = useState(0);
  const [isPrompting, setIsPrompting] = useState(false);

  const isDirtyRef = useRef(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const commit = useCallback((next: ApprovalChainsDrawerView) => {
    isDirtyRef.current = false;
    setView(next);
    if (next.kind === 'editor') setRenderKey((key) => key + 1);
  }, []);

  const guard = useCallback((action: () => void) => {
    if (!isDirtyRef.current) {
      action();
      return;
    }
    pendingActionRef.current = action;
    setIsPrompting(true);
  }, []);

  /** The drawer's entry point, and the editor's way back. */
  const openList = useCallback(() => guard(() => commit({ kind: 'list' })), [guard, commit]);

  const openEditor = useCallback(
    (target: ApprovalChainEditorTarget) =>
      guard(() => {
        commit({ kind: 'editor', target });
      }),
    [guard, commit],
  );

  const close = useCallback(() => guard(() => commit({ kind: 'closed' })), [guard, commit]);

  /** A saved form has nothing left to discard, so this skips the prompt. */
  const afterSave = useCallback(() => {
    commit({ kind: 'list' });
  }, [commit]);

  const setDirty = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  const confirmDiscard = useCallback(() => {
    setIsPrompting(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    isDirtyRef.current = false;
    action?.();
  }, []);

  const cancelDiscard = useCallback(() => {
    setIsPrompting(false);
    pendingActionRef.current = null;
  }, []);

  return {
    view,
    renderKey,
    isPrompting,
    openList,
    openEditor,
    close,
    afterSave,
    setDirty,
    confirmDiscard,
    cancelDiscard,
  };
}

export type ApprovalChainsDrawerController = ReturnType<typeof useApprovalChainsDrawer>;
